// ═══════════════════════════════════════════════════════════════
// ChapterChatPane — Chat éditorial scopé au chapitre (S57-G premium)
// Style carnet : pas de bulles messagerie, typographie fine, actions
// au hover. Micro + send intégrés dans la bordure textarea. Sélecteur
// modèle compact. Vocal via /api/voice/transcribe.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { Send, Check, Copy, Plus, ArrowDown, Mic, Square, Zap, ChevronDown } from 'lucide-react';
import MarkdownMessage from '../chat/MarkdownMessage';
import ScrollToBottomButton from '../chat/ScrollToBottomButton';
import useAudioRecorder from '../../hooks/useAudioRecorder';
import { useStreamChat } from '../../hooks/useStreamChat';
import { appendToChapter, createChapter } from '../../services/bookApi';
import { toast } from '../ui/Toast';

// S57.1 — Kimi K2.5 et K2-thinking retirés du sélecteur Atelier
// (deprecated 25 mai 2026 par OpenRouter / Moonshot, message du 5 mai 2026).
// Seul K2.6 reste, drop-in compatible avec qualité supérieure.
const MODELS = [
    { id: null,                          label: 'Auto',     hint: 'Routage auto' },
    { id: 'moonshotai/kimi-k2.6',        label: 'Kimi K2.6', hint: 'Latest agentique 256K' },
    { id: 'anthropic/claude-sonnet-4.6', label: 'Sonnet 4.6', hint: 'Éditorial ~$0.02' },
    { id: 'anthropic/claude-opus-4.7',   label: 'Opus 4.7', hint: 'Recherche ~$0.15' },
    { id: 'openai/gpt-5',                label: 'GPT-5',    hint: 'Alternative' },
];

const MSG_KEY = (chapterId) => `itriia_chapter_chat_msgs_v1_${chapterId || 'none'}`;
const MSG_MAX = 60; // garde les N derniers pour éviter de gonfler localStorage

const ChapterChatPane = ({ projectId, bookId, chapter, onChapterUpdated, onChapterCreated }) => {
    // Charge l'historique depuis localStorage au 1er mount ET à chaque changement de chapitre
    const [messages, setMessages] = useState(() => {
        try {
            const raw = localStorage.getItem(MSG_KEY(chapter?.id));
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    });

    // Rehydrate les messages quand on change de chapitre
    useEffect(() => {
        try {
            const raw = localStorage.getItem(MSG_KEY(chapter?.id));
            setMessages(raw ? JSON.parse(raw) : []);
        } catch { setMessages([]); }
    }, [chapter?.id]);

    // Persiste les messages à chaque changement (tronqué aux N derniers)
    useEffect(() => {
        if (!chapter?.id) return;
        try {
            const toPersist = messages.slice(-MSG_MAX);
            localStorage.setItem(MSG_KEY(chapter.id), JSON.stringify(toPersist));
        } catch {}
    }, [messages, chapter?.id]);
    const [input, setInput] = useState('');
    const [actionPending, setActionPending] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [transcribing, setTranscribing] = useState(false);
    const [forcedModel, setForcedModel] = useState(() => {
        const v = localStorage.getItem('itriia_chapter_chat_model_v1');
        // Purge explicite de l'ancien choix Haiku (retiré du sélecteur)
        if (v === 'anthropic/claude-haiku-4.5') return null;
        return v && MODELS.find(m => m.id === v) ? v : null;
    });
    const [showModelMenu, setShowModelMenu] = useState(false);
    const listRef = useRef(null);
    const modelMenuRef = useRef(null);
    const textareaRef = useRef(null);
    const { sendStreamMessage, streamingContent, isStreaming } = useStreamChat();
    const { isRecording, seconds, audioBlob, startRecording, stopRecording, resetRecording, formatTime } = useAudioRecorder();

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, streamingContent]);

    useEffect(() => {
        if (!showModelMenu) return;
        const h = (e) => { if (modelMenuRef.current && !modelMenuRef.current.contains(e.target)) setShowModelMenu(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [showModelMenu]);

    useEffect(() => {
        try { localStorage.setItem('itriia_chapter_chat_model_v1', forcedModel || ''); } catch {}
    }, [forcedModel]);

    // Transcription vocale : dès qu'on a un audioBlob, transcribe + inject dans textarea
    useEffect(() => {
        if (!audioBlob) return;
        if (seconds < 1) { resetRecording(); return; } // clic accidentel
        (async () => {
            setTranscribing(true);
            try {
                const form = new FormData();
                form.append('file', audioBlob, 'recording.webm');
                const token = localStorage.getItem('oracle_token');
                const res = await fetch('/api/voice/transcribe', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: form,
                });
                const data = await res.json();
                if (data?.text) {
                    setInput(v => (v.trim() ? `${v} ${data.text}` : data.text).trim());
                    setTimeout(() => textareaRef.current?.focus(), 0);
                } else {
                    toast.error('Transcription vide');
                }
            } catch (err) {
                toast.error(`Transcription échouée : ${err.message}`);
            } finally {
                setTranscribing(false);
                resetRecording();
            }
        })();
    }, [audioBlob]);

    const currentModel = MODELS.find(m => m.id === forcedModel) || MODELS[0];

    const handleSend = async () => {
        const msg = input.trim();
        if (!msg || isStreaming || !chapter) return;
        const userMsg = { id: `u-${Date.now()}`, role: 'user', content: msg };
        setMessages(m => [...m, userMsg]);
        setInput('');
        try {
            const result = await sendStreamMessage(`/api/projects/${projectId}/chat/stream`, {
                message: msg,
                mode: 'comm',
                current_chapter_id: chapter.id,
                forced_model: forcedModel || undefined,
            });
            const finalContent = result?.content || result?.response || '';
            if (finalContent) {
                setMessages(m => [...m, { id: `a-${Date.now()}`, role: 'assistant', content: finalContent }]);
            }
        } catch (err) {
            setMessages(m => [...m, { id: `e-${Date.now()}`, role: 'assistant', content: `Erreur : ${err.message || 'inconnue'}` }]);
        }
    };

    const handleInsert = async (m) => {
        if (!chapter || actionPending) return;
        setActionPending(m.id + ':insert');
        try {
            const res = await appendToChapter(bookId, chapter.id, m.content);
            if (res?.chapter && onChapterUpdated) onChapterUpdated(res.chapter);
            toast.success('Ajouté au chapitre');
        } catch (e) { toast.error(e.detail?.message || e.message || 'Erreur'); }
        finally { setActionPending(null); }
    };
    const handleCopy = (m) => {
        navigator.clipboard.writeText(m.content);
        setCopiedId(m.id);
        setTimeout(() => setCopiedId(null), 1500);
    };
    const handleNewChapter = async (m) => {
        if (!bookId || actionPending) return;
        setActionPending(m.id + ':new');
        try {
            const first = (m.content.match(/^#+\s*(.+)$/m) || m.content.split('\n').find(l => l.trim()) || 'Nouveau chapitre').replace(/^#+\s*/, '').trim();
            const title = first.substring(0, 120) || 'Nouveau chapitre';
            const res = await createChapter(bookId, { title, content_text: m.content });
            if (res?.chapter && onChapterCreated) onChapterCreated(res.chapter);
            toast.success(`Créé : ${title}`);
        } catch (e) { toast.error(e.detail?.message || e.message || 'Erreur'); }
        finally { setActionPending(null); }
    };

    const toggleMic = () => {
        if (isRecording) stopRecording();
        else if (!transcribing && !isStreaming) startRecording();
    };

    return (
        <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            minHeight: 0, minWidth: 0,
            background: 'var(--card-bg-secondary)',
        }}>
            {/* Scope subtil — 1 ligne, discret */}
            <div style={{
                padding: '8px 16px', fontSize: 10,
                color: 'var(--text-muted)', letterSpacing: 0.3,
                borderBottom: '1px solid var(--card-border)',
                display: 'flex', alignItems: 'center', gap: 6,
            }}>
                <span style={{ width: 5, height: 5, borderRadius: 3, background: '#a78bfa' }} />
                <span style={{ textTransform: 'uppercase', fontWeight: 600, fontSize: 9 }}>
                    {chapter ? `${chapter.position}. ${chapter.title}` : 'projet entier'}
                </span>
                <span style={{ opacity: 0.6 }}>· posture COM active</span>
            </div>

            {/* Conversation — style carnet */}
            <div ref={listRef} style={{ flex: 1, overflow: 'auto', padding: '14px 20px', position: 'relative' }}>
                {messages.length === 0 && !isStreaming && (
                    <div style={{
                        color: 'var(--text-muted)', fontSize: 12,
                        fontStyle: 'italic', textAlign: 'center', marginTop: 60,
                        lineHeight: 1.7, fontFamily: 'Georgia, serif',
                    }}>
                        Demande-moi d'écrire, réviser, transitionner, résumer.<br />
                        <span style={{ fontSize: 10, opacity: 0.7 }}>La posture COM/Écrivain oriente chaque réponse.</span>
                    </div>
                )}
                {messages.map(m => (
                    <div
                        key={m.id}
                        onMouseEnter={() => setHoveredId(m.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        style={{
                            marginBottom: 16,
                            paddingLeft: m.role === 'user' ? 0 : 12,
                            borderLeft: m.role === 'assistant' ? '2px solid rgba(139,92,246,0.35)' : 'none',
                        }}
                    >
                        {m.role === 'user' ? (
                            <div style={{
                                fontSize: 12, color: 'var(--text-secondary)',
                                fontStyle: 'italic', lineHeight: 1.5,
                                fontFamily: 'Georgia, serif',
                                whiteSpace: 'pre-wrap',
                            }}>
                                › {m.content}
                            </div>
                        ) : (
                            <>
                                <div style={{
                                    fontSize: 13, color: 'var(--text)',
                                    fontFamily: 'Georgia, serif', lineHeight: 1.65,
                                }}>
                                    <MarkdownMessage content={m.content} />
                                </div>
                                {/* Actions discrètes au hover */}
                                <div style={{
                                    display: 'flex', gap: 4, marginTop: 6,
                                    opacity: hoveredId === m.id || actionPending?.startsWith(m.id) ? 1 : 0,
                                    transition: 'opacity 0.15s',
                                }}>
                                    <button onClick={() => handleInsert(m)} disabled={!!actionPending || !chapter} title="Insérer au chapitre" style={actBtn}>
                                        <ArrowDown size={10} /> {actionPending === m.id + ':insert' ? '…' : 'Insérer'}
                                    </button>
                                    <button onClick={() => handleCopy(m)} title="Copier" style={actBtn}>
                                        {copiedId === m.id ? <Check size={10} /> : <Copy size={10} />}
                                        {copiedId === m.id ? 'Copié' : 'Copier'}
                                    </button>
                                    <button onClick={() => handleNewChapter(m)} disabled={!!actionPending || !bookId} title="Nouveau chapitre" style={actBtn}>
                                        <Plus size={10} /> {actionPending === m.id + ':new' ? '…' : 'Nouveau'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
                {isStreaming && (
                    <div style={{ paddingLeft: 12, borderLeft: '2px solid rgba(16,185,129,0.55)' }}>
                        {/* Indicateur IA en cours — dot vert pulsant, s'affiche dès le début du flux */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            fontSize: 10, color: '#10b981',
                            letterSpacing: 0.4, textTransform: 'uppercase',
                            fontWeight: 600, marginBottom: 8,
                        }}>
                            <span style={{
                                width: 7, height: 7, borderRadius: 4,
                                background: '#10b981',
                                boxShadow: '0 0 8px #10b981, 0 0 14px rgba(16,185,129,0.6)',
                                animation: 'itriiaPulse 1.1s ease-in-out infinite',
                            }} />
                            <span>ITRIIA {streamingContent ? 'rédige' : 'réfléchit'}…</span>
                        </div>
                        {streamingContent && (
                            <div style={{ fontSize: 13, color: 'var(--text)', fontFamily: 'Georgia, serif', lineHeight: 1.65 }}>
                                <MarkdownMessage content={streamingContent} />
                            </div>
                        )}
                    </div>
                )}
                {/* S57 — Bouton ⬇️ scroll-to-bottom factorisé (Atelier) */}
                <ScrollToBottomButton containerRef={listRef} />
            </div>

            {/* Zone saisie premium : mic gauche / textarea centre / send droite — tout dans la même bordure */}
            <div style={{ padding: '10px 14px 12px' }}>
                <div style={{
                    position: 'relative',
                    border: `1px solid ${isRecording ? '#ef4444' : 'var(--card-border)'}`,
                    borderRadius: 10,
                    background: 'var(--card-bg)',
                    transition: 'border 0.15s',
                }}>
                    {/* Mic à gauche (absolute) */}
                    <button
                        onClick={toggleMic}
                        disabled={transcribing || isStreaming}
                        title={isRecording ? `Arrêter (${formatTime(seconds)})` : transcribing ? 'Transcription…' : 'Dicter'}
                        style={{
                            position: 'absolute', left: 8, top: 10,
                            width: 28, height: 28, borderRadius: 6,
                            background: isRecording ? 'rgba(239,68,68,0.15)' : 'transparent',
                            border: 'none',
                            color: isRecording ? '#ef4444' : transcribing ? '#a78bfa' : 'var(--text-muted)',
                            cursor: transcribing || isStreaming ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 2,
                            animation: isRecording ? 'pulse 1.4s infinite' : 'none',
                        }}
                    >
                        {isRecording ? <Square size={12} /> : <Mic size={13} />}
                    </button>

                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                        }}
                        placeholder={transcribing ? 'Transcription en cours…' : isRecording ? `Enregistrement ${formatTime(seconds)}…` : 'Demande à l\'IA sur ce chapitre…'}
                        disabled={isStreaming || transcribing}
                        rows={2}
                        style={{
                            width: '100%', padding: '10px 44px 10px 44px',
                            background: 'transparent', color: 'var(--text)',
                            border: 'none', outline: 'none',
                            fontSize: 13, resize: 'none', lineHeight: 1.5,
                            fontFamily: 'Georgia, serif',
                            opacity: (isStreaming || transcribing) ? 0.6 : 1,
                        }}
                    />

                    {/* Send à droite (absolute) */}
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || isStreaming || transcribing}
                        title="Envoyer"
                        style={{
                            position: 'absolute', right: 8, top: 10,
                            width: 28, height: 28, borderRadius: 6,
                            background: input.trim() && !isStreaming && !transcribing ? 'rgba(139,92,246,0.2)' : 'transparent',
                            border: 'none',
                            color: input.trim() && !isStreaming && !transcribing ? '#c4b5fd' : 'var(--text-muted)',
                            cursor: input.trim() && !isStreaming && !transcribing ? 'pointer' : 'not-allowed',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <Send size={12} />
                    </button>
                </div>

                {/* Barre modèle + raccourci — sous la boîte, compacte */}
                <div ref={modelMenuRef} style={{
                    position: 'relative', marginTop: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <button
                        onClick={() => setShowModelMenu(v => !v)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 3,
                            padding: '2px 6px', borderRadius: 3,
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-muted)', fontSize: 10, cursor: 'pointer',
                        }}
                    >
                        <Zap size={9} style={{ color: '#a78bfa' }} />
                        <span style={{ color: '#a78bfa', fontWeight: 500 }}>{currentModel.label}</span>
                        <ChevronDown size={8} />
                    </button>
                    <span style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 0.3 }}>
                        Enter ↵ · Shift+Enter pour ligne
                    </span>
                    {showModelMenu && (
                        <div style={{
                            position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
                            background: 'var(--card-bg)',
                            border: '1px solid var(--card-border)',
                            borderRadius: 6, padding: 3, minWidth: 200,
                            boxShadow: '0 6px 24px rgba(0,0,0,0.3)', zIndex: 30,
                        }}>
                            {MODELS.map(m => (
                                <button
                                    key={m.id || 'auto'}
                                    onClick={() => { setForcedModel(m.id); setShowModelMenu(false); }}
                                    style={{
                                        width: '100%', textAlign: 'left',
                                        padding: '5px 8px', borderRadius: 4,
                                        background: forcedModel === m.id ? 'rgba(139,92,246,0.12)' : 'transparent',
                                        border: 'none',
                                        color: forcedModel === m.id ? '#a78bfa' : 'var(--text)',
                                        fontSize: 11, cursor: 'pointer',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                                    }}
                                >
                                    <span style={{ fontWeight: 500 }}>{m.label}</span>
                                    <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{m.hint}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <style>{`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                    @keyframes itriiaPulse {
                        0%, 100% {
                            transform: scale(1);
                            box-shadow: 0 0 8px #10b981, 0 0 14px rgba(16,185,129,0.6);
                        }
                        50% {
                            transform: scale(1.35);
                            box-shadow: 0 0 14px #34d399, 0 0 22px rgba(52,211,153,0.9);
                        }
                    }
                `}</style>
            </div>
        </div>
    );
};

const actBtn = {
    display: 'flex', alignItems: 'center', gap: 3,
    padding: '2px 7px', borderRadius: 3, fontSize: 10,
    background: 'transparent',
    border: '1px solid transparent',
    color: 'var(--text-muted)', cursor: 'pointer',
    transition: 'all 0.15s',
};

export default ChapterChatPane;
